"use client";
import { useRef, useCallback, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_HOME_CATEGORIES } from '@/graphql/queries';
import { AWS_URL } from '@/constants';
import { defaultImage } from '@/utility/productDetailHelpers';
import { useRouter, usePathname } from 'next/navigation';
import { ChevronRight } from 'lucide-react';

export default function MobileCategorySlider() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  // Hide-on-scroll state
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);

  const { data, loading, error } = useQuery(GET_HOME_CATEGORIES);

  const getCategoryIconUrl = useCallback((icon: any) => {
    if (icon?.path) {
      return AWS_URL && icon.path ? `${AWS_URL}${icon.path}` : defaultImage;
    }
    return defaultImage;
  }, []);

  const categories = data?.product_categories
    ?.filter((cat: any) => cat.is_active && !cat.deleted_at)
    ?.map((cat: any) => ({
      id: cat.id,
      name: cat.name,
      image: getCategoryIconUrl(cat.icon),
    })) || [];

  // Hide on scroll-down, show on scroll-up
  useEffect(() => {
    const handleWindowScroll = () => {
      const currentY = window.scrollY;
      if (currentY < 10) {
        setVisible(true);
      } else if (currentY > lastScrollY.current + 4) {
        setVisible(false);
      } else if (currentY < lastScrollY.current - 4) {
        setVisible(true);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, []);

  const handleCategoryClick = (category: any) => {
    router.push(`/shop-category/${category.id}`);
  };

  if (pathname !== '/') return null;

  if (loading) {
    return (
      <div className="lg:hidden bg-white border-b border-gray-100 py-3 px-4 transition-transform duration-300">
        <div className="flex gap-4 overflow-x-auto hide-scrollbar">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="flex-shrink-0">
              <div className="w-20 h-20 bg-gray-100 rounded-2xl animate-pulse" />
              <div className="w-16 h-3 bg-gray-100 rounded mt-2 mx-auto animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || categories.length === 0) return null;

  return (
    <div
      className="lg:hidden bg-white border-b border-gray-100 shadow-sm transition-transform duration-300 ease-in-out"
      style={{ transform: visible ? 'translateY(0)' : 'translateY(-110%)', marginTop: '1.5rem' }}
    >
      <div
        ref={scrollContainerRef}
        className="flex gap-3 overflow-x-auto hide-scrollbar py-3 px-4 scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {categories.map((category: any) => (
          <button
            key={category.id}
            onClick={() => handleCategoryClick(category)}
            className="flex-shrink-0 flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
          >
            <div className="relative w-[72px] h-[72px] bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl overflow-hidden shadow-sm group-active:shadow-md transition-shadow">
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
              <div className="absolute inset-0 flex items-center justify-center p-3">
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).src = defaultImage; }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-amber-600/5 to-transparent opacity-0 group-active:opacity-100 transition-opacity" />
            </div>
            <span className="text-[11px] font-medium text-gray-600 text-center leading-tight max-w-[72px] line-clamp-2 group-active:text-amber-600 transition-colors">
              {category.name}
            </span>
          </button>
        ))}

        {/* View All */}
        <button
          onClick={() => router.push('/shop')}
          className="flex-shrink-0 flex flex-col items-center justify-center gap-1.5 group active:scale-95 transition-transform"
        >
          <div className="w-[72px] h-[72px] bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center shadow-sm group-active:shadow-md transition-shadow border border-gray-200">
            <ChevronRight className="w-6 h-6 text-gray-400 group-active:text-gray-600 transition-colors" />
          </div>
          <span className="text-[11px] font-medium text-gray-600 text-center leading-tight group-active:text-gray-900 transition-colors">
            View All
          </span>
        </button>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}